import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { io as socketIOClient } from 'socket.io-client';
import LoginPage from './LoginPage';
import UsuariosPage from './UsuariosPage';
import BaseDatosPage from './BaseDatosPage';
import CodificarPage from './CodificarPage';
import CrearUsuarioPage from './CrearUsuarioPage';
import CapturaPage from './CapturaPage';
import PendientesForaneoPage from './PendientesForaneoPage';
import PendientesLocalPage from './PendientesLocalPage';
import {
  MultiasistenciaCambiosPage,
  MultiasistenciaCancelacionesPage,
  MultiasistenciaFacturacionPage,
  MultiasistenciaPendientesPage,
  MultiasistenciaRecuperarEfectivoPage,
  MultiasistenciaTodoPage,
  MultiasistenciaForaneoCambiosPage,
  MultiasistenciaForaneoCancelacionesPage,
  MultiasistenciaForaneoFacturacionPage,
  MultiasistenciaForaneoPendientesPage,
  MultiasistenciaForaneoRecuperarEfectivoPage,
  MultiasistenciaForaneoTodoPage
} from './MultiasistenciaPage';
import {
  QualitasXnuupLocalPage,
  QualitasXnuupForaneoPage
} from './QualitasXnuupPage';
import FacturacionPage from './FacturacionPage';
import CancelacionesPage from './CancelacionesPage';
import NotificacionesPage from './NotificacionesPage';
import { API_BASE_URL, SOCKET_URL } from './config';
import './App.css';

const HEALTH_CHECK_INTERVAL_MS = 15000;
const HEALTH_CHECK_TIMEOUT_MS = 6000;
const OFFLINE_MESSAGE = 'Sin conexión con el servidor. Intentando reconectar…';
const BACKEND_ERROR_MESSAGE = 'El servidor respondió, pero con un error interno.';
const DEFAULT_APP_TITLE = 'Panel de gestión';
const SERVICE_WORKER_PATH = '/notification-sw.js';
const ASSET_MANIFEST_PATH = '/asset-manifest.json';
const BUILD_VERSION_CHECK_INTERVAL_MS = 30000;
const FORCE_RELOAD_ON_OPEN_KEY = 'rav.forceReloadOnOpen';
const FORCE_RELOAD_DONE_KEY = 'rav.forceReloadDone';
const SKIP_BEFORE_UNLOAD_FLAG_KEY = 'rav.skipBeforeUnloadFlag';

const isConnectivityIssue = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  // Browsers typically raise TypeError for network issues / CORS blocks.
  if (error instanceof TypeError) return true;
  const message = (error.message || '').toLowerCase();
  return message.includes('networkerror') || message.includes('failed to fetch');
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

function ActionHeader({
  onBackToMenu,
  showBack,
  onCerrarSesion,
  pendingNotificaciones,
  notificationsSupported,
  notifyPermission,
  onEnableBrowserNotifications
}) {
  const notificationLinkClass = `app-notification-link${window.location.pathname.startsWith('/notificaciones') ? ' app-notification-link--active' : ''}`;
  return (
    <header className="app-header compact-header">
      <div className="compact-header__left">
        {showBack && (
          <button type="button" className="btn-ghost" onClick={onBackToMenu}>
            ← Regresar al menú
          </button>
        )}
      </div>
      <div className="compact-header__right">
        {notificationsSupported && notifyPermission !== 'granted' && (
          <button type="button" className="btn-ghost" onClick={onEnableBrowserNotifications}>
            Activar alertas
          </button>
        )}
        <a href="/notificaciones" className={notificationLinkClass}>
          <span>Notificaciones</span>
          {pendingNotificaciones > 0 && <span className="app-notification-dot">{pendingNotificaciones}</span>}
        </a>
        <button className="app-header__logout" onClick={onCerrarSesion}>
          Cerrar sesión
        </button>
      </div>
    </header>
  );
}

function MenuGrid({ usuario }) {
  const usuarioSistema = String(usuario?.username || usuario?.usuario || '').trim().toLowerCase();
  const esDuenasSistema = usuarioSistema === 'programas' || usuarioSistema === 'karina';
  const navLinkClass = (extra = '') => ({ isActive }) =>
    `app-nav__link${isActive ? ' app-nav__link--active' : ''}${extra ? ` ${extra}` : ''}`;

  return (
    <div className="menu-grid">
      <h1 className="menu-title">Menú principal</h1>
      <div className="menu-grid__buttons">
        <div className="menu-grid__buttons-main">
          <NavLink to="/basedatos" className={navLinkClass('app-nav__link--database')}>
            Base de datos
          </NavLink>
          <NavLink to="/codificar" className={navLinkClass()}>
            Codificar
          </NavLink>
          <NavLink to="/pendientes-foraneo" className={navLinkClass()}>
            INPART FORÁNEO
          </NavLink>
          <NavLink to="/pendientes-local" className={navLinkClass()}>
            INPART LOCAL
          </NavLink>
          <NavLink to="/facturacion" className={navLinkClass()}>
            Facturación
          </NavLink>
          <NavLink to="/cancelaciones" className={navLinkClass()}>
            Cancelaciones
          </NavLink>
          <NavLink to="/captura" className={navLinkClass()}>
            Captura
          </NavLink>
          {esDuenasSistema && (
            <>
              <NavLink to="/usuarios" className={navLinkClass()}>
                Gestión de Usuarios
              </NavLink>
              <NavLink to="/crear-usuario" className={navLinkClass()}>
                Crear Usuario
              </NavLink>
            </>
          )}
        </div>
        <div className="menu-grid__buttons-side">
          <NavLink to="/multiasistencia" className={navLinkClass('app-nav__link--multiasistencia')}>
            MULTIASISTENCIA LOCAL
          </NavLink>
          <NavLink to="/multiasistencia-foraneo" className={navLinkClass('app-nav__link--multiasistencia')}>
            MULTIASISTENCIA FORÁNEO
          </NavLink>
          <NavLink to="/qualitas-xnuup" className={navLinkClass('app-nav__link--multiasistencia')}>
            QUALITAS/XNUUP LOCAL
          </NavLink>
          <NavLink to="/qualitas-xnuup-foraneo" className={navLinkClass('app-nav__link--multiasistencia')}>
            QUALITAS/XNUUP FORÁNEO
          </NavLink>
        </div>
      </div>
    </div>
  );
}

function ToastStack({ toasts, onDismiss }) {
  const navigate = useNavigate();
  if (!toasts.length) return null;

  const handleOpen = (toast) => {
    const target = toast.target;
    let search = '';
    if (target?.scope && target?.notificacionId) {
      const params = new URLSearchParams({
        focusScope: target.scope,
        focusId: String(target.notificacionId)
      });
      search = `?${params.toString()}`;
    }
    navigate(`/notificaciones${search}`);
    onDismiss?.(toast.id);
  };

  return (
    <div className="app-toast-stack" role="region" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className="app-toast">
          <button
            type="button"
            className="app-toast__body"
            onClick={() => handleOpen(toast)}
          >
            {toast.title && <strong>{toast.title}</strong>}
            {toast.message && <span>{toast.message}</span>}
          </button>
          <button
            type="button"
            className="app-toast__close"
            onClick={() => onDismiss?.(toast.id)}
            aria-label="Cerrar alerta"
          >
            x
          </button>
        </div>
      ))}
    </div>
  );
}

function AppInner() {
  const [usuario, setUsuario] = useState(() => {
    const user = localStorage.getItem('usuario');
    return user ? JSON.parse(user) : null;
  });
  const [backendOffline, setBackendOffline] = useState(false);
  const [pendingNotificaciones, setPendingNotificaciones] = useState(0);
  const [notificacionesRefreshToken, setNotificacionesRefreshToken] = useState(0);
  const [notificationToasts, setNotificationToasts] = useState([]);
  const browserNotificationsSupported = typeof window !== 'undefined' && 'Notification' in window;
  const pushApiAvailable =
    browserNotificationsSupported && typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const [browserNotifyPermission, setBrowserNotifyPermission] = useState(() => {
    if (!browserNotificationsSupported) return 'unsupported';
    return Notification.permission;
  });
  const defaultFaviconRef = useRef(null);
  const defaultTitleRef = useRef('');
  const serviceWorkerReadyRef = useRef(null);
  const vapidKeyRef = useRef(null);
  const pushSubscriptionRef = useRef(null);
  const buildVersionReloadedRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionStorage.getItem(SKIP_BEFORE_UNLOAD_FLAG_KEY) === '1') {
        sessionStorage.removeItem(SKIP_BEFORE_UNLOAD_FLAG_KEY);
        return;
      }
      localStorage.setItem(FORCE_RELOAD_ON_OPEN_KEY, '1');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const shouldReloadOnOpen = localStorage.getItem(FORCE_RELOAD_ON_OPEN_KEY) === '1';
    const reloadAlreadyDone = sessionStorage.getItem(FORCE_RELOAD_DONE_KEY) === '1';

    if (!shouldReloadOnOpen) {
      sessionStorage.removeItem(FORCE_RELOAD_DONE_KEY);
      return;
    }

    if (reloadAlreadyDone) {
      localStorage.removeItem(FORCE_RELOAD_ON_OPEN_KEY);
      sessionStorage.removeItem(FORCE_RELOAD_DONE_KEY);
      return;
    }

    sessionStorage.setItem(FORCE_RELOAD_DONE_KEY, '1');
    sessionStorage.setItem(SKIP_BEFORE_UNLOAD_FLAG_KEY, '1');
    window.location.reload();
  }, []);

  const dismissToast = useCallback((id) => {
    setNotificationToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback(
    ({ title, message, duration = 5000, target }) => {
      if (!title && !message) return;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setNotificationToasts((prev) => [...prev, { id, title, message, target }]);
      setTimeout(() => dismissToast(id), duration);
    },
    [dismissToast]
  );

  const bumpNotificationsRefresh = useCallback(() => {
    setNotificacionesRefreshToken(Date.now());
  }, []);

  const updateFaviconBadge = useCallback(
    (count) => {
      if (typeof document === 'undefined') return;
      let link = document.querySelector("link[rel*='icon']");
      if (!defaultFaviconRef.current) {
        defaultFaviconRef.current = link ? link.href : '/favicon.ico';
      }
      if (!defaultTitleRef.current) {
        defaultTitleRef.current = document.title || DEFAULT_APP_TITLE;
      }
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }

      if (!count) {
        link.href = defaultFaviconRef.current;
        document.title = defaultTitleRef.current;
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, 64, 64);
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(48, 16, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 20px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(count > 9 ? '9+' : String(count), 48, 16);
      link.href = canvas.toDataURL('image/png');
      document.title = `(${count}) ${defaultTitleRef.current}`;
    },
    []
  );

  const fireBrowserNotification = useCallback(
    ({ title, message }) => {
      if (!browserNotificationsSupported) return;
      if (Notification.permission !== 'granted') return;
      try {
        new Notification(title || 'Notificación', {
          body: message || '',
          icon: '/favicon.ico',
          badge: '/favicon.ico'
        });
      } catch (error) {
        console.warn('No se pudo mostrar la notificación del navegador', error);
      }
    },
    [browserNotificationsSupported]
  );

  const getServiceWorkerRegistration = useCallback(async () => {
    if (!pushApiAvailable) return null;
    if (serviceWorkerReadyRef.current) {
      return serviceWorkerReadyRef.current;
    }
    try {
      const registration = await navigator.serviceWorker.ready;
      serviceWorkerReadyRef.current = registration;
      return registration;
    } catch (error) {
      console.warn('El service worker de notificaciones no está listo:', error);
      return null;
    }
  }, [pushApiAvailable]);

  const ensureVapidPublicKey = useCallback(async () => {
    if (!pushApiAvailable) return null;
    if (vapidKeyRef.current) return vapidKeyRef.current;
    try {
      const response = await fetch(`${API_BASE_URL}/api/push/public-key`);
      if (!response.ok) {
        throw new Error('No se pudo obtener la llave pública VAPID');
      }
      const data = await response.json();
      if (!data.publicKey) {
        throw new Error('La llave pública VAPID no está configurada en el servidor');
      }
      vapidKeyRef.current = data.publicKey;
      return vapidKeyRef.current;
    } catch (error) {
      console.warn('No se pudo descargar la llave pública VAPID:', error);
      return null;
    }
  }, [pushApiAvailable]);

  const subscribeUserToPush = useCallback(async () => {
    if (!pushApiAvailable || !usuario?.username) return null;
    try {
      const registration = await getServiceWorkerRegistration();
      if (!registration) return null;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        const publicKey = await ensureVapidPublicKey();
        if (!publicKey) return null;
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
      }
      pushSubscriptionRef.current = subscription;
      await fetch(`${API_BASE_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: usuario.username, subscription })
      });
      return subscription;
    } catch (error) {
      console.warn('No se pudo registrar la suscripción push:', error);
      return null;
    }
  }, [usuario, pushApiAvailable, ensureVapidPublicKey, getServiceWorkerRegistration]);

  const unsubscribeUserFromPush = useCallback(async () => {
    if (!pushApiAvailable) return;
    try {
      const registration = await getServiceWorkerRegistration();
      if (!registration) return;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;
      const endpoint = subscription.endpoint;
      await fetch(`${API_BASE_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint })
      }).catch((error) => {
        console.warn('No se pudo informar al servidor que se eliminó la suscripción push:', error);
      });
      await subscription.unsubscribe();
      pushSubscriptionRef.current = null;
    } catch (error) {
      console.warn('No se pudo cancelar la suscripción push:', error);
    }
  }, [pushApiAvailable, getServiceWorkerRegistration]);

  const enableBrowserNotifications = useCallback(async () => {
    if (!browserNotificationsSupported) return;
    try {
      const result = await Notification.requestPermission();
      setBrowserNotifyPermission(result);
      if (result === 'granted') {
        await subscribeUserToPush();
      }
    } catch (error) {
      console.warn('No se pudo solicitar el permiso de notificaciones del navegador', error);
    }
  }, [browserNotificationsSupported, subscribeUserToPush]);

  const fetchPendingNotificaciones = useCallback(async () => {
    if (!usuario?.username) return;
    try {
      const params = new URLSearchParams({ usuario: usuario.username, estado: 'pendiente' });
      const response = await fetch(`${API_BASE_URL}/api/notificaciones?${params.toString()}`, {
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new Error('Respuesta no válida');
      }
      const data = await response.json();
      const pendientesInternas = Array.isArray(data)
        ? data.filter((item) => String(item?.remitente || '').toUpperCase() !== 'SISTEMA').length
        : 0;
      setPendingNotificaciones(pendientesInternas);
    } catch (error) {
      console.warn('No se pudo obtener el total de notificaciones pendientes', error);
    }
  }, [usuario]);

  const handlePendingSync = useCallback((valor) => {
    setPendingNotificaciones(Math.max(0, Number.isFinite(valor) ? valor : 0));
  }, []);

  const handleCerrarSesion = useCallback(() => {
    setUsuario(null);
    setPendingNotificaciones(0);
    setNotificationToasts([]);
    setNotificacionesRefreshToken(0);
    unsubscribeUserFromPush();
  }, [unsubscribeUserFromPush]);

  useEffect(() => {
    if (usuario) {
      localStorage.setItem('usuario', JSON.stringify(usuario));
    } else {
      localStorage.removeItem('usuario');
    }
  }, [usuario]);

  useEffect(() => {
    if (!browserNotificationsSupported) return;
    setBrowserNotifyPermission(Notification.permission);
  }, [browserNotificationsSupported]);

  useEffect(() => {
    if (!pushApiAvailable) return;
    let cancelled = false;
    const registerWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
        if (!cancelled) {
          serviceWorkerReadyRef.current = registration;
        }
      } catch (error) {
        console.warn('No se pudo registrar el service worker de notificaciones:', error);
      }
    };
    registerWorker();
    navigator.serviceWorker.ready
      .then((registration) => {
        if (!cancelled) {
          serviceWorkerReadyRef.current = registration;
        }
      })
      .catch((error) => {
        console.warn('El service worker no está listo:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [pushApiAvailable]);

  useEffect(() => {
    if (!usuario) {
      setPendingNotificaciones(0);
      return;
    }
    fetchPendingNotificaciones();
  }, [usuario, fetchPendingNotificaciones]);

  useEffect(() => {
    if (!usuario || !notificacionesRefreshToken) return;
    fetchPendingNotificaciones();
  }, [usuario, notificacionesRefreshToken, fetchPendingNotificaciones]);

  useEffect(() => {
    updateFaviconBadge(pendingNotificaciones);
  }, [pendingNotificaciones, updateFaviconBadge]);

  useEffect(() => {
    if (!usuario?.username) return;
    if (!pushApiAvailable) return;
    if (browserNotifyPermission !== 'granted') return;
    subscribeUserToPush();
  }, [usuario, pushApiAvailable, browserNotifyPermission, subscribeUserToPush]);

  useEffect(() => {
    if (usuario) return;
    if (!pushApiAvailable) return;
    unsubscribeUserFromPush();
  }, [usuario, pushApiAvailable, unsubscribeUserFromPush]);

  useEffect(() => {
    let isMounted = true;
    let activeController = null;

    const checkHealth = async () => {
      if (!isMounted) return;
      if (activeController) {
        activeController.abort();
      }
      const controller = new AbortController();
      activeController = controller;
      const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);
      try {
        const response = await fetch(`${API_BASE_URL}/api/health`, {
          signal: controller.signal,
          cache: 'no-store'
        });
        if (!response.ok) {
          if (isMounted) {
            console.warn(`${BACKEND_ERROR_MESSAGE} (HTTP ${response.status})`);
            setBackendOffline(false);
          }
        } else if (isMounted) {
          setBackendOffline(false);
        }
      } catch (err) {
        if (!isMounted) return;
        if (isConnectivityIssue(err)) {
          setBackendOffline(true);
        } else {
          console.warn(BACKEND_ERROR_MESSAGE, err);
          setBackendOffline(false);
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };

    checkHealth();
    const intervalId = setInterval(checkHealth, HEALTH_CHECK_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      if (activeController) {
        activeController.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!usuario?.username) return;

    const socket = socketIOClient(SOCKET_URL, {
      transports: ['websocket'],
      autoConnect: true
    });

    const handleUpdate = (payload = {}) => {
      const currentUser = usuario.username;
      const tipo = payload.tipo;

      if (tipo === 'nueva') {
        const destinatarios = Array.isArray(payload.destinatarios) ? payload.destinatarios : [];
        if (destinatarios.includes(currentUser)) {
          pushToast({
            title: 'Nueva notificación',
            message: payload.mensaje || `${payload.remitente || 'Un usuario'} te envió un mensaje.`,
            target: payload.notificacionId
              ? { scope: 'inbox', notificacionId: payload.notificacionId }
              : undefined
          });
          fireBrowserNotification({
            title: 'Nueva notificación',
            message: payload.mensaje || `${payload.remitente || 'Un usuario'} te envió un mensaje.`
          });
          if (String(payload.remitente || '').toUpperCase() !== 'SISTEMA') {
            setPendingNotificaciones((prev) => prev + 1);
          }
          bumpNotificationsRefresh();
        }
        return;
      }

      if (tipo === 'atendida') {
        const esDestinatario = payload.destinatario === currentUser;
        const esRemitente = payload.remitente === currentUser;
        if (esRemitente) {
          pushToast({
            title: 'Notificación atendida',
            message: payload.mensaje || `${payload.destinatario} marcó tu notificación como atendida.`,
            target: payload.notificacionId
              ? { scope: 'sent', notificacionId: payload.notificacionId }
              : undefined
          });
          fireBrowserNotification({
            title: 'Notificación atendida',
            message: payload.mensaje || `${payload.destinatario} marcó tu notificación como atendida.`
          });
        }
        if (esDestinatario || esRemitente) {
          if (esDestinatario) {
            setPendingNotificaciones((prev) => Math.max(prev - 1, 0));
          }
          bumpNotificationsRefresh();
        }
        return;
      }

      // Para eventos futuros mantenemos la bandeja sincronizada si afectan al usuario
      bumpNotificationsRefresh();
    };

    socket.on('notificaciones_actualizadas', handleUpdate);

    return () => {
      socket.off('notificaciones_actualizadas', handleUpdate);
      socket.disconnect();
    };
  }, [usuario, pushToast, bumpNotificationsRefresh, fireBrowserNotification]);

  useEffect(() => {
    const handlePageShow = (event) => {
      const navigationEntries = performance.getEntriesByType('navigation');
      const navigationType = navigationEntries[0]?.type;
      const restoredFromSession = event.persisted || navigationType === 'back_forward';

      if (restoredFromSession) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    const getCurrentMainBundlePath = () => {
      const mainScript = document.querySelector("script[src*='/static/js/main.']");
      if (!mainScript) return '';
      try {
        return new URL(mainScript.src, window.location.origin).pathname;
      } catch (_error) {
        return '';
      }
    };

    const checkForNewBuild = async () => {
      if (buildVersionReloadedRef.current) return;

      try {
        const response = await fetch(ASSET_MANIFEST_PATH, { cache: 'no-store' });
        if (!response.ok) return;

        const manifest = await response.json();
        const expectedMainPath = manifest?.files?.['main.js'];
        const currentMainPath = getCurrentMainBundlePath();

        if (!expectedMainPath || !currentMainPath) return;
        if (currentMainPath === expectedMainPath) return;

        buildVersionReloadedRef.current = true;
        window.location.reload();
      } catch (_error) {
        // Ignore transient errors and retry on the next visibility/focus check.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForNewBuild();
      }
    };

    checkForNewBuild();
    const intervalId = window.setInterval(checkForNewBuild, BUILD_VERSION_CHECK_INTERVAL_MS);
    window.addEventListener('focus', checkForNewBuild);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', checkForNewBuild);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const bannerVisible = backendOffline;
  const bannerMessage = OFFLINE_MESSAGE;

  const offlineBanner = bannerVisible ? (
    <div className="app-offline-banner" role="status">
      {bannerMessage}
    </div>
  ) : null;

  const offlineSpacer = bannerVisible ? <div className="app-shell__banner-spacer" aria-hidden="true" /> : null;
  const usuarioSistema = String(usuario?.username || usuario?.usuario || '').trim().toLowerCase();
  const esDuenasSistema = usuarioSistema === 'programas' || usuarioSistema === 'karina';

  const location = useLocation();
  const navigate = useNavigate();
  const atMenu = location.pathname === '/menu';

  return (
    <div className="app-shell">
      {offlineBanner}
      {offlineSpacer}
      <ToastStack toasts={notificationToasts} onDismiss={dismissToast} />
      {!usuario ? (
        <LoginPage onLogin={setUsuario} />
      ) : (
        <div className="app-container">
          <div className="app-hero-brand">
            <div className="app-hero-brand__title">RAV AUTOPARTES</div>
            <div className="app-hero-brand__welcome">Bienvenido, {usuario?.username || ''}</div>
          </div>
          <ActionHeader
            showBack={!atMenu}
            onBackToMenu={() => navigate('/menu')}
            onCerrarSesion={handleCerrarSesion}
            pendingNotificaciones={pendingNotificaciones}
            notificationsSupported={pushApiAvailable}
            notifyPermission={browserNotifyPermission}
            onEnableBrowserNotifications={enableBrowserNotifications}
          />
          <main className="app-main">
            <div className="app-card">
              <Routes>
                <Route path="/" element={<Navigate to="/menu" replace />} />
                <Route path="/menu" element={<MenuGrid usuario={usuario} />} />
                <Route path="/multiasistencia" element={<Navigate to="/multiasistencia/todo" replace />} />
                <Route path="/multiasistencia/todo" element={<MultiasistenciaTodoPage />} />
                <Route path="/multiasistencia/pendientes" element={<MultiasistenciaPendientesPage />} />
                <Route path="/multiasistencia/cancelaciones" element={<MultiasistenciaCancelacionesPage />} />
                <Route path="/multiasistencia/cambios-por-recuperar" element={<MultiasistenciaCambiosPage />} />
                <Route path="/multiasistencia/recuperar-efectivo" element={<MultiasistenciaRecuperarEfectivoPage />} />
                <Route path="/multiasistencia/facturacion" element={<MultiasistenciaFacturacionPage />} />

                <Route path="/multiasistencia-foraneo" element={<Navigate to="/multiasistencia-foraneo/todo" replace />} />
                <Route path="/multiasistencia-foraneo/todo" element={<MultiasistenciaForaneoTodoPage />} />
                <Route path="/multiasistencia-foraneo/pendientes" element={<MultiasistenciaForaneoPendientesPage />} />
                <Route path="/multiasistencia-foraneo/cancelaciones" element={<MultiasistenciaForaneoCancelacionesPage />} />
                <Route path="/multiasistencia-foraneo/cambios-por-recuperar" element={<MultiasistenciaForaneoCambiosPage />} />
                <Route path="/multiasistencia-foraneo/recuperar-efectivo" element={<MultiasistenciaForaneoRecuperarEfectivoPage />} />
                <Route path="/multiasistencia-foraneo/facturacion" element={<MultiasistenciaForaneoFacturacionPage />} />

                <Route path="/qualitas-xnuup" element={<QualitasXnuupLocalPage />} />
                <Route path="/qualitas-xnuup-foraneo" element={<QualitasXnuupForaneoPage />} />
                <Route path="/basedatos" element={<BaseDatosPage />} />
                <Route path="/captura" element={<CapturaPage />} />
                <Route path="/pendientes-foraneo" element={<PendientesForaneoPage />} />
                <Route path="/pendientes-local" element={<PendientesLocalPage />} />
                <Route path="/facturacion" element={<FacturacionPage />} />
                <Route path="/cancelaciones" element={<CancelacionesPage />} />
                <Route
                  path="/notificaciones"
                  element={
                    <NotificacionesPage
                      usuario={usuario}
                      onPendingChange={handlePendingSync}
                      refreshToken={notificacionesRefreshToken}
                    />
                  }
                />
                {esDuenasSistema && (
                  <Route path="/usuarios" element={<UsuariosPage />} />
                )}
                <Route path="/codificar" element={<CodificarPage />} />
                {esDuenasSistema && (
                  <Route path="/crear-usuario" element={<CrearUsuarioPage />} />
                )}
                <Route path="*" element={<Navigate to="/menu" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      )}
    </div>
  );
}

function AppRoot() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

export default AppRoot;
